import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { LocationBackModel, LocationState } from '../types/location-state';

/**
 * The custom hook can be used for goBack Operations.
 * It will check if the location has the back link then
 * it will do `history.push(backLink)` else `it will push to fallback route.`
 *
 * @constructor
 * fallbackDestination: string
 * fallbackText: string
 *
 * @returns
 * back() => void;
 *
 * text: string
 */
function useGoBack(
    fallbackDestination: string,
    fallbackText: string,
): { back: () => void; text: string } {
    const history = useHistory();

    const location = useLocation<LocationState>();

    const back = useMemo<LocationBackModel | undefined>(() => {
        const lastIndex = (location.state?.back?.length || 0) - 1;
        if (lastIndex >= 0) {
            return location.state?.back?.[lastIndex];
        }
    }, [location]);

    const handleClick = useCallback(() => {
        const link = location.state?.back?.pop()?.link;
        history.push(link ?? fallbackDestination, location.state);
    }, [history, fallbackDestination, location]);

    const backText = useMemo(
        () => back?.text ?? fallbackText,
        [back, fallbackText],
    );

    return { back: handleClick, text: backText };
}

export default useGoBack;
